import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { API_ENDPOINTS } from './client/endpoints';
import client from './client';
import { GenerateImageQueryOptions } from '@/types';

export const useGeneratedImages = (options: GenerateImageQueryOptions) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery(
    ['generated-images', options],
    async ({ pageParam = 1 }) => {
      const response = await client.generatedImages.all({
        ...options,
        page: pageParam,
      });
      return {
        data: response.data,
        totalCount: response.total,
        currentPage: pageParam,
        lastPage: response.last_page,
      };
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.currentPage < lastPage.lastPage) {
          return lastPage.currentPage + 1;
        }
        return undefined;
      },
      enabled: options.enabled ?? true,
    },
  );

  const generatedImages = data?.pages.flatMap(page => page.data) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    generatedImages,
    totalCount,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useGenerateImage = (options?: {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { prompt: string; product_id: string; image_size: string; additional_lora_product_ids?: number[]; is_public: boolean; num_images: number; }) =>
      client.ai.generate(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([API_ENDPOINTS.USERS_ME]);
        options?.onSuccess?.(data);
      },
      onError: options?.onError,
    }
  );
};

export const useQueueStatus = (requestId: string | null, options?: {
  enabled?: boolean;
  refetchInterval?: number;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
  onSettled?: (data: any) => void;
}) => {
  return useQuery(
    [API_ENDPOINTS.QUEUE_STATUS, requestId],
    () => client.ai.getQueueStatus(requestId!),
    {
      enabled: !!requestId && options?.enabled,
      refetchInterval: options?.refetchInterval,
      onSuccess: options?.onSuccess,
      onSettled: options?.onSettled,
      onError: options?.onError,
    }
  );
};

export const useQueueResponse = (requestId: string | null, options?: {
  enabled?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}) => {
  return useQuery(
    [API_ENDPOINTS.QUEUE_RESPONSE, requestId],
    () => client.ai.getQueueResponse(requestId!),
    {
      enabled: !!requestId && options?.enabled,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    }
  );
};

export const useUpdateImagesPublicStatus = (options?: {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { image_ids: number[]; is_public: boolean }) =>
      client.generatedImages.updatePublicStatus(data),
    {
      onSuccess: (data) => {
        // Invalidate both my images and public images queries to refresh the data
        queryClient.invalidateQueries(['generated-images']);
        options?.onSuccess?.(data);
      },
      onError: options?.onError,
    }
  );
};

export const useToggleImageLike = (options?: {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, imageIndex }: { id: number; imageIndex: number }) =>
      client.generatedImages.toggleLike(id, imageIndex),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['generated-images']);
        options?.onSuccess?.(data);
      },
      onError: options?.onError,
    }
  );
};

export const useReportImage = (options?: {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}) => {
  return useMutation(
    ({ id, imageIndex, message }: { id: number; imageIndex: number; message: string }) =>
      client.generatedImages.report(id, imageIndex, message),
    {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    }
  );
};